import { MonthRow, Scenario } from '../../domain/types.js';
import { effectiveRatePeriods } from '../../domain/timeline.js';
import { formatCompactRub, formatMonthLabel, formatPercent, formatRub, formatTerm } from '../format.js';
import {
  ChartMode,
  EMPTY_TEXT,
  HAIRLINE_STROKE,
  MODES,
  assignLabelRows,
  createFrame,
  drawXAxis,
  drawYAxis,
  marker,
  renderEmpty,
} from './frame.js';
import { linearScale, niceTicks } from './scales.js';
import { svgEl } from './svg.js';
import { TooltipRow, attachCrosshair } from './tooltip.js';

export type RateTone = 'base' | 'discount' | 'refinance';

export type RateSegment = {
  start: number;
  end: number;
  rate: number;
  tone: RateTone;
  label: string;
};

export type RateChartInput = {
  months: MonthRow[];
  scenario: Scenario;
};

type EventMark = { month: number; text: string };

const TONE_LABELS: Record<RateTone, string> = {
  base: 'обычная ставка',
  discount: 'льготная ставка',
  refinance: 'после рефинансирования',
};

function toneFor(monthIndex: number, scenario: Scenario): RateTone {
  if (effectiveRatePeriods(scenario).some(p => monthIndex >= p.startMonth && monthIndex <= p.endMonth)) {
    return 'discount';
  }
  if (scenario.refinances.some(refi => monthIndex >= refi.month)) {
    return 'refinance';
  }
  return 'base';
}

/** Splits the schedule into constant-rate runs; tones come from scenario data, not labels. */
export function buildRateSegments(months: MonthRow[], scenario: Scenario): RateSegment[] {
  const segments: RateSegment[] = [];
  if (!months.length) return segments;
  let start = 0;
  let rate = months[0].rateApr;
  let tone = toneFor(0, scenario);
  for (let i = 1; i < months.length; i += 1) {
    const nextTone = toneFor(i, scenario);
    if (months[i].rateApr !== rate || nextTone !== tone) {
      segments.push({ start, end: i - 1, rate, tone, label: TONE_LABELS[tone] });
      start = i;
      rate = months[i].rateApr;
      tone = nextTone;
    }
  }
  segments.push({ start, end: months.length - 1, rate, tone, label: TONE_LABELS[tone] });
  return segments;
}

function collectEvents(scenario: Scenario, count: number): EventMark[] {
  const events: EventMark[] = [];
  effectiveRatePeriods(scenario).forEach(period => {
    events.push({ month: period.startMonth, text: `Льготная ставка ${formatPercent(period.rateApr)}` });
  });
  scenario.refinances.forEach(refi => {
    events.push({ month: refi.month, text: `Рефинансирование: ${formatPercent(refi.newRateApr)}, ${formatTerm(refi.newTermMonths)}` });
  });
  scenario.overpayments
    .filter(op => !op.recurring && !op.adaptive && op.amount > 0)
    .forEach(op => events.push({ month: op.month, text: `Разовый платёж ${formatCompactRub(op.amount)}` }));
  return events.filter(event => event.month >= 0 && event.month < count).sort((a, b) => a.month - b.month);
}

export function render(container: HTMLElement, input: RateChartInput, mode: ChartMode = 'compact'): void {
  const { months, scenario } = input;
  if (!months.length) {
    renderEmpty(container, EMPTY_TEXT);
    return;
  }

  const count = months.length;
  const cfg = MODES[mode];
  const rowHeight = cfg.fontSize + 5;
  const events = collectEvents(scenario, count);
  const maxRate = months.reduce((max, m) => Math.max(max, m.rateApr), 0);
  const ticks = niceTicks(0, maxRate + 1, cfg.yTicks);
  const yLabels = ticks.map(value => formatPercent(value));

  const frame = createFrame(container, mode, { ariaLabel: 'Ставка и события', yLabels });
  const { svg, plot, fontSize } = frame;

  // Marker labels are placed with the real measured width; the rows they need
  // are reserved above the plot before anything is drawn.
  const xScale = linearScale(0, count, plot.x0, plot.x1);
  const placements = assignLabelRows(events.map(event => ({ x: xScale(event.month), text: event.text })), fontSize, plot);
  const rows = placements.reduce((max, placement) => Math.max(max, placement.row + 1), 0);
  plot.y0 += rows ? rows * rowHeight + 8 : 0;

  const xMid = (i: number) => xScale(i + 0.5);
  const yScale = linearScale(0, ticks[ticks.length - 1], plot.y1, plot.y0);

  drawYAxis(frame, ticks, yScale, value => formatPercent(value));

  const bands = svgEl('g', { class: 'chart-bands' });
  const band = (from: number, to: number, color: string) => {
    const start = Math.max(0, Math.min(count, from));
    const end = Math.max(0, Math.min(count, to));
    if (end <= start) return;
    bands.append(svgEl('rect', {
      x: xScale(start).toFixed(1),
      y: plot.y0.toFixed(1),
      width: (xScale(end) - xScale(start)).toFixed(1),
      height: (plot.y1 - plot.y0).toFixed(1),
      fill: color,
      'fill-opacity': 0.08,
    }));
  };
  const refinances = [...scenario.refinances].sort((a, b) => a.month - b.month);
  refinances.forEach((refi, index) => {
    band(refi.month, refinances[index + 1]?.month ?? count, 'var(--ember)');
  });
  effectiveRatePeriods(scenario).forEach(period => band(period.startMonth, period.endMonth + 1, 'var(--lime)'));
  svg.append(bands);

  const segments = buildRateSegments(months, scenario);
  const parts: string[] = [];
  segments.forEach((segment, index) => {
    const y = yScale(segment.rate).toFixed(1);
    if (index === 0) parts.push(`M${xScale(segment.start).toFixed(1)},${y}`);
    else parts.push(`V${y}`);
    parts.push(`H${xScale(segment.end + 1).toFixed(1)}`);
  });
  svg.append(svgEl('path', {
    d: parts.join(' '),
    fill: 'none',
    stroke: 'var(--series-current)',
    'stroke-width': 2,
    'stroke-linejoin': 'round',
    'stroke-linecap': 'round',
    'data-series': 'current',
  }));

  // Event markers live in a row above the plot so they never cover the line.
  const markers = svgEl('g', { class: 'chart-events' });
  events.forEach((event, index) => {
    const x = xScale(event.month);
    const placement = placements[index];
    markers.append(svgEl('line', {
      x1: x.toFixed(1),
      x2: x.toFixed(1),
      y1: plot.y0.toFixed(1),
      y2: plot.y1.toFixed(1),
      stroke: HAIRLINE_STROKE,
      'stroke-width': 1,
      'stroke-dasharray': '2,3',
    }));
    markers.append(svgEl('polygon', {
      points: `${(x - 4).toFixed(1)},${(plot.y0 - 8).toFixed(1)} ${(x + 4).toFixed(1)},${(plot.y0 - 8).toFixed(1)} ${x.toFixed(1)},${(plot.y0 - 1).toFixed(1)}`,
      fill: 'var(--text-muted)',
    }));
    markers.append(svgEl('text', {
      x: placement.x.toFixed(1),
      y: (cfg.margin.top + placement.row * rowHeight + fontSize - 1).toFixed(1),
      'text-anchor': placement.anchor,
      'font-size': fontSize,
      fill: 'var(--text-muted)',
    }, [event.text]));
  });
  svg.append(markers);

  drawXAxis(frame, months, { xForIndex: xScale });

  const focus = marker(0, 0, 'var(--series-current)');
  focus.setAttribute('visibility', 'hidden');
  svg.append(focus);

  attachCrosshair({
    svg,
    host: container,
    plot,
    count,
    xForIndex: xMid,
    renderTooltip: (i): TooltipRow[] => {
      const month = months[i];
      const segment = segments.find(s => i >= s.start && i <= s.end);
      return [
        { value: formatMonthLabel(month.dateLabel) },
        { value: formatPercent(month.rateApr), label: 'Ставка', color: 'var(--series-current)' },
        { value: segment?.label ?? TONE_LABELS.base, label: 'Период' },
        { value: formatRub(month.scheduledPayment), label: 'Обязательный платёж' },
      ];
    },
    onIndex: i => {
      if (i === null) {
        focus.setAttribute('visibility', 'hidden');
        return;
      }
      focus.setAttribute('cx', xMid(i).toFixed(1));
      focus.setAttribute('cy', yScale(months[i].rateApr).toFixed(1));
      focus.setAttribute('visibility', 'visible');
    },
  });
}
